import { QueryClientProvider } from "@tanstack/react-query";
import { Suspense, lazy, useMemo } from "react";
import { FormProvider } from "react-hook-form";
import { Toaster } from "react-hot-toast";
import { queryClient } from "../../../query";
import { getTrainingInputSchema } from "../../../schema";
import type { AccessToken, Prediction, Version } from "../../../types";
import {
  PlaygroundContext,
  type PlaygroundContextValue,
} from "../../api-playground/context";
import { useCreateTrainingForm, type TrainingAction } from "../shared";

const TrainingForm = lazy(() => import("../training-form"));

export default function GenericTraining({
  authenticated,
  version,
  owner,
  avatar,
  token,
  destinations,
  training,
  trainingVersion,
}: {
  authenticated: boolean;
  version: Version;
  owner: string;
  avatar: string;
  token: AccessToken | null;
  destinations?: { name: string; username: string }[];
  training?: Prediction;
  trainingVersion?: Version;
}) {
  const trainingInputSchema = getTrainingInputSchema(version);

  const action: TrainingAction =
    training && trainingVersion ? "update" : "create";

  const destination = trainingVersion
    ? `${trainingVersion._extras.model.owner}/${trainingVersion._extras.model.name}`
    : null;

  const formHeading = useMemo(() => {
    if (action === "create") return "Create training";
    if (action === "update" && destination) {
      return `Retrain ${destination}`;
    }

    return "Create training";
  }, [action, destination]);

  const formDescription = useMemo(() => {
    if (action === "create")
      return `The easiest way to train ${version._extras.model.owner}/${version._extras.model.name} is to use the form below. Upon
    creation, you will be redirected to the training detail page
    where you can monitor your training's progress, and eventually
    download the weights and run the trained model.`;

    if (action === "update") {
      const modelDescription = destination || "your model";
      return `The easiest way to retrain ${modelDescription} is to use the form below. A new version of the model will be created, and you will be redirected to the training detail page where you can monitor your training's progress, and eventually download the weights and run the trained model.`;
    }
  }, [action, destination, version]);

  const form = useCreateTrainingForm({
    action,
    training,
    trainingVersion,
    version,
  });

  const context: PlaygroundContextValue = {
    elementVisibility: {},
    features: {},
    hideAdvancedInputs: false,
    hideVersionMismatchWarning: false,
    isAuthenticated: true,
    modelStatus: null,
    permissions: {
      create_example: false,
      debug: false,
      delete: false,
      report: version._extras.model.visibility === "public",
      run: false,
      share: true,
      tweak: true,
      can_edit_featured_inputs: false,
    },
    renderMode: "default",
    token: token ?? null,
    version: version,
  };

  return (
    <PlaygroundContext.Provider value={context}>
      <Toaster />
      <QueryClientProvider client={queryClient}>
        <div className="flex flex-col md:flex-row gap-8">
          {action === "create" && (
            <div className="w-full md:max-w-lg md:order-1">
              <div className="text-r8-sm bg-r8-gray-2 p-4 border border-r8-gray-10 text-r8-gray-12">
                <p>
                  If you haven’t yet trained a model on Replicate, we recommend
                  you read one of the following guides.
                </p>
                <ul className="list-disc pl-4 mt-2">
                  <li>
                    <a href="/docs/guides/fine-tune-an-image-model">
                      Fine-tune an image model
                    </a>
                  </li>
                  <li>
                    <a href="/blog/fine-tune-flux">
                      Fine-tune Flux with your own images
                    </a>
                  </li>
                  <li>
                    <a href="/blog/fine-tune-sdxl">
                      Fine-tune SDXL with your own images
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          )}
          <div className="w-full md:max-w-2xl lg:flex-shrink-0">
            <div className="mb-6">
              <h4 className="!my-0 text-r8-xl font-semibold">{formHeading}</h4>
              <div className="text-r8-sm text-r8-gray-12 space-y-2">
                <p>{formDescription}</p>
              </div>
            </div>
            <Suspense>
              <FormProvider {...form}>
                <TrainingForm
                  trainingVersion={trainingVersion}
                  version={version}
                  authenticated={authenticated}
                  owner={owner}
                  avatar={avatar}
                  token={token}
                  destinations={destinations}
                  action={action}
                  properties={trainingInputSchema?.properties || {}}
                />
              </FormProvider>
            </Suspense>
          </div>
        </div>
      </QueryClientProvider>
    </PlaygroundContext.Provider>
  );
}
